import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { loadFromLocalStorage, saveToLocalStorage } from '../utils/helpers';
import { STORAGE_KEYS } from '../constants';
import db from '../utils/firestore';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { attemptLinkPendingGoogle } from '../utils/authLinking';

// Define the user interface
export interface User {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  profileImage: string;
  profileId?: string; // Member ID
  accountId?: string; // Account ID
  googleAuth?: boolean; // Indicates if user authenticated via Google
  firmName?: string; // Law firm or organization name
  role?: string; // attorney | paralegal | legal_admin | other
}

/**
 * Options for logout(). `returnTo` is a same-origin app path (with query) the
 * post-wipe hard navigation lands on instead of '/'. Added 2026-08-12 for the
 * invite journey's "use a different account": the accept page signs the
 * current account out and must resume at /auth with the invite's ?redirect=
 * intact — the comprehensive wipe still runs in full either way.
 */
export interface LogoutOptions {
  returnTo?: string;
}

// Define the auth context type
interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (user: User) => Promise<void>;
  logout: (options?: LogoutOptions) => void;
}

// Create the context with default undefined value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props interface
interface AuthProviderProps {
  children: ReactNode;
}

// Create the AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize state with values from localStorage to maintain persistence
  // CRITICAL FIX: Check both token AND user data exist together for authentication
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const token = loadFromLocalStorage(STORAGE_KEYS.AUTH_TOKEN, null);
    const savedUser = loadFromLocalStorage('user_data', null);

    // User is only authenticated if BOTH token and user data exist
    return token !== null && savedUser !== null && typeof savedUser === 'object';
  });
  
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = loadFromLocalStorage('user_data', null);
    if (savedUser && typeof savedUser === 'object') {
      // Cast to any first to access properties safely
      const userData = savedUser as any;
      
      // Handle backward compatibility for existing users
      // If they have a name but no firstName/lastName, parse the name
      if (userData.name && typeof userData.name === 'string' && 
          (!userData.firstName || !userData.lastName)) {
        const nameParts = userData.name.split(' ');
        userData.firstName = nameParts[0] || '';
        userData.lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      }
      
      return userData as User;
    }
    return null;
  });

  /**
   * 2026-08-15: clear the cached identity — state + every localStorage key
   * that makes `isAuthenticated` true. Mirrors logout()'s synchronous wipe.
   * Called whenever Firebase authoritatively reports signed out while the
   * cache still claims a user; without this, the cache (backed by a
   * client-minted token nothing expires) impersonates a session forever.
   */
  const clearCachedIdentity = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    try { localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN); } catch {}
    try { localStorage.removeItem('user_data'); } catch {}
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('appCache:') && k.endsWith(':user_data')) keys.push(k);
      }
      keys.forEach((k) => { try { localStorage.removeItem(k); } catch {} });
    } catch {}
  }, []);

  // Effect to handle authentication state validation on mount and changes
  useEffect(() => {
    const token = loadFromLocalStorage(STORAGE_KEYS.AUTH_TOKEN, null);
    const savedUser = loadFromLocalStorage('user_data', null);
    
    // Validate authentication state consistency
    const shouldBeAuthenticated = token !== null && savedUser !== null && typeof savedUser === 'object';
    
    if (shouldBeAuthenticated !== isAuthenticated) {
      console.log('Correcting authentication state inconsistency');
      setIsAuthenticated(shouldBeAuthenticated);
    }
    
    // If we have user data but no user state, restore it
    if (shouldBeAuthenticated && !user && savedUser) {
      console.log('Restoring user data from localStorage');
      const userData = savedUser as any;
      
      // Handle backward compatibility
      if (userData.name && typeof userData.name === 'string' && 
          (!userData.firstName || !userData.lastName)) {
        const nameParts = userData.name.split(' ');
        userData.firstName = nameParts[0] || '';
        userData.lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      }
      
      setUser(userData as User);
    }
    
    // If we don't have valid auth data but state says we're authenticated, fix it
    if (!shouldBeAuthenticated && isAuthenticated) {
      console.log('Clearing invalid authentication state');
      setIsAuthenticated(false);
      setUser(null);
    }
  }, [isAuthenticated, user]); // Added missing dependencies

  // Save authentication state to localStorage when it changes
  useEffect(() => {
    if (isAuthenticated && user) {
      // Save both token and user data when authenticated
      saveToLocalStorage('user_data', user);
      
      // Ensure we have a valid token
      const existingToken = loadFromLocalStorage(STORAGE_KEYS.AUTH_TOKEN, null);
      if (!existingToken) {
        const token = `auth_${Math.random().toString(36).substring(2)}`;
        saveToLocalStorage(STORAGE_KEYS.AUTH_TOKEN, token);
        console.log('Generated new auth token during state save');
      }
    }
    // On logout, a comprehensive wipe is performed elsewhere
  }, [isAuthenticated, user]);

  // Track if we've synced with Firebase to avoid duplicate syncs
  const firebaseSyncedRef = useRef(false);
  // Use refs to access current values without stale closures
  const isAuthenticatedRef = useRef(isAuthenticated);
  const userRef = useRef(user);
  
  // Keep refs in sync with state
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
    userRef.current = user;
  }, [isAuthenticated, user]);
  
  // CRITICAL: Sync with Firebase auth on mount to restore session
  // This runs once on app load to ensure Firebase-restored sessions are reflected in AuthContext
  useEffect(() => {
    if (firebaseSyncedRef.current) return;
    
    const auth = getAuth();
    
    // Set up a one-time listener to catch Firebase's auth restoration
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const currentIsAuthenticated = isAuthenticatedRef.current;
      const currentUser = userRef.current;

      // 2026-08-15 owner incident (second occurrence): a null/anonymous
      // Firebase state used to be silently ignored here, so the cached
      // localStorage identity outlived the real session forever — portal
      // pages opened for signed-out visitors, the auth page greeted them
      // with a stale "welcome back", and the wallet rendered empty. Firebase
      // is the authority: when it says signed out, the cache clears NOW.
      if (!firebaseUser || firebaseUser.isAnonymous) {
        if (currentIsAuthenticated || currentUser) {
          console.log('🚫 Firebase reports signed out — clearing stale cached identity');
          clearCachedIdentity();
        }
        return;
      }
      
      // If we already have the correct user in context, we're good
      if (currentIsAuthenticated && currentUser?.id === firebaseUser.uid) {
        console.log('✅ AuthContext already synced with Firebase user:', firebaseUser.uid);
        firebaseSyncedRef.current = true;
        return;
      }
      
      // If Firebase has a user but context doesn't, restore the session
      if (!currentIsAuthenticated || currentUser?.id !== firebaseUser.uid) {
        console.log('🔄 Restoring auth session from Firebase user:', firebaseUser.uid);
        
        // Build user from Firebase auth
        const display = firebaseUser.displayName || '';
        const [first, ...rest] = display ? display.split(' ') : [''];
        const restoredUser: User = {
          id: firebaseUser.uid,
          name: display || (firebaseUser.email || firebaseUser.uid),
          firstName: first || '',
          lastName: rest.length > 0 ? rest.join(' ') : '',
          email: firebaseUser.email || '',
          profileImage: firebaseUser.photoURL || '',
          googleAuth: firebaseUser.providerData.some(p => p.providerId === 'google.com')
        };
        
        try {
          await login(restoredUser);
          console.log('✅ Auth session restored from Firebase');
        } catch (err) {
          console.error('Failed to restore auth session from Firebase:', err);
        }
      }
      
      firebaseSyncedRef.current = true;
    });
    
    return () => {
      try { unsubscribe(); } catch {}
    };
  }, []); // Empty deps - run once on mount, uses refs for current values
  
  // Bridge Firebase auth events to context login for non-anonymous users
  const handleAuthBridge = useCallback(async (e: Event) => {
      try {
        // Custom event from setupAuthObserver
        const detail = (e as CustomEvent).detail as { user: { uid: string; isAnonymous: boolean } | null };
        const payload = detail ? detail.user : null;
        if (!payload) {
          // 2026-08-15: a broadcast sign-out is authoritative — clearing was
          // deferred to "the logout flow" here, but token expiry, another
          // tab's sign-out, and incognito session death never RUN the logout
          // flow, which is how the cached identity outlived the session.
          if (isAuthenticated || user) {
            console.log('🚫 Auth observer broadcast signed out — clearing stale cached identity');
            clearCachedIdentity();
          }
          return;
        }
        if (payload.isAnonymous) return; // do not treat anonymous as logged-in account
        
        // Check if already authenticated with the SAME user
        if (isAuthenticated && user?.id === payload.uid) return;

        const auth = getAuth();
        const cu = auth.currentUser;
        if (!cu || cu.uid !== payload.uid) return;

        // Build minimal user model and delegate to login() to ensure profile/doc creation
        const display = cu.displayName || '';
        const [first, ...rest] = display ? display.split(' ') : [''];
        const builtUser = {
          id: cu.uid,
          name: display || (cu.email || cu.uid),
          firstName: first || '',
          lastName: rest.length > 0 ? rest.join(' ') : '',
          email: cu.email || '',
          profileImage: cu.photoURL || '',
          googleAuth: true
        } as User;

        try {
          await login(builtUser);
        } catch (err) {
          console.warn('AuthContext bridge: login failed', err);
        }
      } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, clearCachedIdentity]);

  useEffect(() => {
    window.addEventListener('app:auth', handleAuthBridge as EventListener);
    return () => { window.removeEventListener('app:auth', handleAuthBridge as EventListener); };
  }, [handleAuthBridge]);

  // Login function with strict data isolation
  const login = async (userData: User) => {
    console.log('🔐 Starting login with strict data isolation...');
    
    // CRITICAL: Initialize user data isolation manager
    try {
      const { userDataIsolation } = await import('../utils/userDataIsolation');
      userDataIsolation.initialize();
      console.log('📧 Login initiated for email:', userData.email);
    } catch (error) {
      console.error('Failed to initialize user data isolation:', error);
    }

    // Create a token (in a real app, this would come from the backend)
    const token = `auth_${Math.random().toString(36).substring(2)}`;
    
    // CRITICAL: Clear any data from previous sessions before proceeding
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('user_data') || key.includes('appCache:') || key.includes('profile'))) {
          // Don't remove the auth token itself yet
          if (!key.includes(STORAGE_KEYS.AUTH_TOKEN)) {
            keysToRemove.push(key);
          }
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`🧹 Cleared ${keysToRemove.length} keys from previous sessions`);
    } catch (error) {
      console.error('Error clearing previous session data:', error);
    }
    
    // Try to hydrate profile/account IDs from Firestore using the canonical UID
    let accountIdFromDb: string | undefined;
    let memberIdFromDb: string | undefined;
    let emailFromDb: string | undefined;
    try {
      if (userData.id) {
        // Guard: ensure we only access profile doc under the authenticated UID
        try { (await import('../lib/identity/writes')).addUserScopedWriteGuard(userData.id!, 'AuthContext.login.hydrate'); } catch {}
        const ref = doc(db as any, 'users', userData.id, 'user_profile', 'user_profile');
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() as any;
          const p = Array.isArray(data?.profiles) ? data.profiles[0] : null;
          if (p) {
            accountIdFromDb = p.account_id || undefined;
            memberIdFromDb = p.member_id || undefined;
            emailFromDb = p.email || userData.email || undefined;
          }
        }
      }
    } catch (e) {
      console.warn('Auth login: failed to hydrate/create profile for UID.', e);
    }

    // Associate known identifiers with the authentication (do not create new IDs here)
    const enrichedUserData = {
      ...userData,
      profileId: memberIdFromDb,
      accountId: accountIdFromDb,
      // Ensure the googleAuth flag is preserved
      googleAuth: !!userData.googleAuth,
      // Prefer email from Firestore profile if present, otherwise use provided
      email: emailFromDb || userData.email
    };
    
    // CRITICAL: Save BOTH token and user data atomically BEFORE setting state
    // This ensures persistence happens even if component unmounts due to navigation
    saveToLocalStorage(STORAGE_KEYS.AUTH_TOKEN, token);
    // Save to BOTH namespaced AND non-namespaced keys for persistence
    // Non-namespaced is required for initialization check, namespaced prevents cross-account leakage
    saveToLocalStorage('user_data', enrichedUserData);
    try { localStorage.setItem(`appCache:${userData.id || 'anon'}:user_data`, JSON.stringify(enrichedUserData)); } catch {}

    // Update state - this will trigger the useEffect above to maintain consistency
    setUser(enrichedUserData);
    setIsAuthenticated(true);

    // Attempt to link any pending Google credential saved during provider mismatch flows
    try { await attemptLinkPendingGoogle(getAuth()); } catch {}
    
    console.log(`User logged in: ${enrichedUserData.email} (${userData.googleAuth ? 'Google account' : 'Email account'})`);
    if (enrichedUserData.accountId && enrichedUserData.profileId) {
      console.log(`Account ID: ${enrichedUserData.accountId}, Member ID: ${enrichedUserData.profileId}`);
    } else {
      console.log('Account/Member IDs not yet hydrated at login; Profile will load them from Firestore.');
    }
    console.log('Authentication state: Token and user data saved to localStorage');
    
    // Dispatch custom event for other components to react to
    window.dispatchEvent(new CustomEvent('auth-state-changed', { 
      detail: { isAuthenticated: true, user: enrichedUserData }
    }));
  };

  // Logout function with comprehensive cleanup
  const logout = (options?: LogoutOptions) => {
    console.log('🔐 Starting comprehensive logout process...');
    // 2026-08-21 sign-out leak: capture the departing uid NOW — by the time
    // resetSession runs below, the auth observer has already nulled the
    // module-level uid, so the purge used to run with no uid and skip the
    // member's scoped thread namespace.
    const departingUid = user?.id || null;
    // Same-origin app paths only — anything else falls back to home. Mirrors
    // sanitizeReturnPath (src/lib/authReturn.ts) without importing a module
    // into this hot path.
    const rawReturnTo = options?.returnTo ?? '';
    const returnTo =
      rawReturnTo.startsWith('/') && !rawReturnTo.startsWith('//') && !rawReturnTo.startsWith('/\\')
        ? rawReturnTo
        : '/';
    
    // Phase 1: Immediately clear authentication state
    // This prevents any new operations from starting
    setIsAuthenticated(false);
    setUser(null);
    
    // Immediately remove critical auth artifacts to avoid rehydration races
    try { localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN); } catch {}
    try { localStorage.removeItem('user_data'); } catch {}
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('appCache:') && k.endsWith(':user_data')) keys.push(k);
      }
      keys.forEach((k) => { try { localStorage.removeItem(k); } catch {} });
    } catch {}

    // Notify listeners early to stop any operations
    try {
      window.dispatchEvent(new CustomEvent('auth-state-changed', { 
        detail: { isAuthenticated: false, user: null }
      }));
    } catch {}

    // Phase 2: Perform comprehensive cleanup asynchronously
    void (async () => {
      try {
        // Use the new CleanupManager for comprehensive cleanup
        try {
          const { performCompleteCleanup } = await import('../utils/cleanupManager');
          const stats = await performCompleteCleanup();
          console.log('Cleanup statistics:', stats);
        } catch (error) {
          console.error('CleanupManager error:', error);
        }

        // Best-effort: disable Google auto-select and sign out of Google contexts if present
        try { (window as any).google?.accounts?.id?.disableAutoSelect?.(); } catch {}
        try {
          const g = (window as any).gapi;
          const auth2 = g?.auth2?.getAuthInstance?.();
          if (auth2) { try { await auth2.signOut(); } catch {} }
        } catch {}

        // Best-effort: sign out Firebase (handles both anonymous and linked accounts)
        try { await getAuth().signOut(); } catch {}
        try { (await import('../utils/anonymousAuth')).AnonymousAuthService.getInstance().signOut(); } catch {}

        // Notify app modules to reset any in-memory listeners/stores
        try { (await import('../lib/auth')).resetSession('explicit_logout', departingUid); } catch {}

        // Nuke all client-side site data
        try { (await import('../utils/siteDataWipe')).wipeAllClientData(); } catch {}

        // Verify cleanup in development mode
        if (process.env.NODE_ENV === 'development') {
          try {
            const { verifyCleanup } = await import('../utils/cleanupManager');
            setTimeout(async () => {
              const isClean = await verifyCleanup();
              if (!isClean) {
                console.warn('⚠️ Cleanup verification failed - some data may remain');
              }
            }, 1000);
          } catch {}
        }
      } finally {
        // Force navigation (home, or the caller's returnTo) and ensure a
        // clean runtime. A cross-document replace() IS the clean runtime —
        // the old timed reload raced the in-flight navigation and, on slow
        // networks, reloaded the CURRENT page instead, dropping the
        // returnTo destination (review 2026-08-13). Reload only when we're
        // already at the target and no navigation will happen.
        try {
          const target = `${window.location.origin}${returnTo}`;
          if (window.location.href !== target) {
            window.location.replace(target);
          } else {
            setTimeout(() => {
              console.log('🔄 Reloading page to complete logout...');
              try { window.location.reload(); } catch {}
            }, 100);
          }
        } catch {}
      }
    })();
  };

  // Create the context value
  const contextValue: AuthContextType = {
    isAuthenticated,
    user,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
// Uses function declaration (hoisted) to avoid TDZ errors from circular module imports
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Non-throwing variant for widget components that may mount outside the
// provider (isolated tests, embedded hosts) — same pattern as useMaybeTopUp.
export function useMaybeAuth(): AuthContextType | null {
  return useContext(AuthContext) ?? null;
}